import React, { useState } from 'react';
import { styled } from '@mui/system';
import { useParams } from 'react-router-dom';
import { useServices } from '../../services';
import CircularProgress from 'components/core/CircularProgress';
import NotFoundContent from 'components/core/NotFoundContent';
import PageContent from 'components/core/PageContent';

const IframeWrapper = styled('div')({
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 'calc(100vh - 120px)',
});

const StyledIframe = styled('iframe')({
  flex: 1,
  width: '100%',
  border: 'none',
});

const LoadingWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 'calc(100vh - 120px)',
});

export interface DocsEmbedProps {
  /** Static path appended after the docs base URL, e.g. "platform_community/about/".
   *  If omitted, the React Router wildcard ("*") is used instead, enabling
   *  deep-linking: /help/for_data_users/using_notebooks/ → for_data_users/using_notebooks/ */
  path?: string;
}

export default function DocsEmbed({ path }: DocsEmbedProps): React.ReactElement {
  const { envService } = useServices();
  const { '*': wildcardPath } = useParams();
  const docsUrl = envService.getValue('DOCS_URL') ?? 'https://msd-live.github.io/msdlive-docs';
  const resolvedPath = path ?? wildcardPath ?? '';
  // No more /embed/ prefix — the full docs site is loaded directly.
  const embedUrl = `${docsUrl}/${resolvedPath}`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  if (error) {
    return (
      <PageContent>
        <NotFoundContent />
      </PageContent>
    );
  }

  return (
    <IframeWrapper>
      {loading && (
        <LoadingWrapper>
          <CircularProgress text="Loading documentation..." />
        </LoadingWrapper>
      )}
      <StyledIframe
        src={embedUrl}
        title="MSD-LIVE Documentation"
        style={loading ? { display: 'none' } : undefined}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
      />
    </IframeWrapper>
  );
}